import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Award, TrendingDown, LineChart, Target, ShieldCheck } from "lucide-react";

export default function ComeValutiamo() {
  return (
    <div className="min-h-screen bg-[var(--bdd-bg)]">
      <Seo
        title="Come valutiamo le offerte — Metodologia Deal Score"
        description="Metodologia trasparente del Deal Score di BrickDailyDeals: come calcoliamo il punteggio 0–100 di ogni offerta LEGO e set di costruzioni."
        path="/come-valutiamo"
      />
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1 mb-5">
            <Award size={12} className="text-[#FBD51C]" />
            <span className="text-xs font-mono-bdd uppercase tracking-[0.25em] text-zinc-400">
              Metodologia
            </span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            Come valutiamo le offerte.
          </h1>
          <p className="text-zinc-400 text-lg mt-6 max-w-2xl leading-relaxed">
            Ogni offerta pubblicata su BrickDailyDeals riceve un{" "}
            <b className="text-white">Deal Score</b> da 0 a 100. Non è un&apos;opinione: è
            il risultato di quattro fattori misurabili che spiegano quanto è
            conveniente <em>davvero</em> quel prezzo, oggi.
          </p>
        </div>

        {/* Score bands */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-10">
          <ScoreBand range="90+" label="Affare eccezionale" color="emerald" />
          <ScoreBand range="75–89" label="Ottimo affare" color="gold" />
          <ScoreBand range="60–74" label="Buon affare" color="blue" />
          <ScoreBand range="40–59" label="Discreto" color="zinc" />
          <ScoreBand range="0–39" label="Sconto contenuto" color="stone" />
        </div>

        {/* Formula sections */}
        <div className="space-y-5">
          <FormulaCard
            icon={<TrendingDown className="text-[#FBD51C]" size={20} />}
            title="Sconto attuale (fino a 40 punti)"
            body={
              <>
                La percentuale di sconto rispetto al prezzo di listino incide fino
                a 40 punti sul totale. Il punteggio è lineare: uno sconto del{" "}
                <b>10%</b> vale 8 punti, del <b>25%</b> vale 20, del <b>50% o più</b>{" "}
                vale il massimo. Usiamo il prezzo di listino verificato dallo store
                al momento dell&apos;import (Amazon, eBay, AliExpress) — non il prezzo
                &quot;strike&quot; gonfiato che a volte alcuni venditori usano.
              </>
            }
          />

          <FormulaCard
            icon={<LineChart className="text-[#FBD51C]" size={20} />}
            title="Andamento sotto la media 30 giorni (fino a 30 punti)"
            body={
              <>
                Registriamo il prezzo dell&apos;offerta ogni 6 ore in un archivio
                privato. Se il prezzo attuale è più basso della{" "}
                <b>media dei prezzi degli ultimi 30 giorni</b>, aggiungiamo fino a
                30 punti (30 punti = prezzo il 30% o più sotto la media). Questo
                fattore diventa attivo dopo qualche giorno di raccolta dati.
              </>
            }
          />

          <FormulaCard
            icon={<Target className="text-[#FBD51C]" size={20} />}
            title="Minimo storico (+15 punti bonus)"
            body={
              <>
                Se il prezzo attuale è al livello più basso mai osservato da
                quando tracciamo l&apos;offerta (e abbiamo almeno 3 rilevazioni),
                aggiungiamo un bonus fisso di 15 punti. È il segnale più forte:
                stai comprando al minimo storico.
              </>
            }
          />

          <FormulaCard
            icon={<ShieldCheck className="text-[#FBD51C]" size={20} />}
            title="Desiderabilità della categoria (fino a 15 punti)"
            body={
              <>
                Non tutti i set hanno lo stesso valore percepito. Categorie come{" "}
                <b>LEGO Icons, Ideas, Star Wars, Technic</b> tendono a mantenere il
                prezzo nel tempo e sono più difficili da trovare in offerta: le
                premiamo con più punti. Le altre categorie ricevono un punteggio
                base di 5.
              </>
            }
          />
        </div>

        {/* Trust block */}
        <div className="mt-12 rounded-2xl bg-zinc-900/60 border border-zinc-800 p-6">
          <h2 className="font-display font-black text-2xl text-white mb-3">
            Cosa NON influenza il punteggio
          </h2>
          <ul className="space-y-2 text-zinc-400 text-sm leading-relaxed">
            <li>
              ❌ <b>Le commissioni affiliate:</b> il Deal Score è identico che il
              link sia affiliato o meno. Non abbiamo interesse a gonfiare i
              punteggi delle offerte con margine più alto.
            </li>
            <li>
              ❌ <b>La popolarità (numero di click):</b> un set molto cliccato non
              riceve punti extra. I click influenzano solo la sezione &quot;Hot&quot; in
              homepage, non il punteggio.
            </li>
            <li>
              ❌ <b>La disponibilità del venditore:</b> uno sconto reale conta
              anche se il venditore ha poche scorte.
            </li>
          </ul>
        </div>

        {/* Refresh info */}
        <div className="mt-6 text-sm text-zinc-500 leading-relaxed">
          Il punteggio viene ricalcolato ogni 6 ore e ogni volta che il prezzo
          dell&apos;offerta cambia. Se hai dubbi su un punteggio specifico,
          puoi{" "}
          <Link to="/contatti" className="text-[#FBD51C] underline hover:text-white">
            contattarci
          </Link>{" "}
          e lo verifichiamo insieme.
        </div>
      </div>

      <Footer />
    </div>
  );
}

function ScoreBand({ range, label, color }) {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    gold: "bg-[#FBD51C]/15 text-[#FBD51C] border-[#FBD51C]/40",
    blue: "bg-blue-500/15 text-blue-300 border-blue-500/40",
    zinc: "bg-zinc-800 text-zinc-400 border-zinc-700",
    stone: "bg-zinc-900 text-zinc-500 border-zinc-800",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${map[color]}`}>
      <div className="font-display font-black text-lg leading-none">{range}</div>
      <div className="text-[10px] font-mono-bdd uppercase tracking-widest mt-2 opacity-80">
        {label}
      </div>
    </div>
  );
}

function FormulaCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl bg-zinc-900/40 border border-zinc-800 p-5">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="font-display font-black text-lg text-white">{title}</h3>
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}
